


























































































































































































































import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import { Referral } from '@/api/model/referral'
import { User } from '@/api/model/user'
import { Action } from '@/api/model/action'
import { generateChannelOptions, InputSelectOption } from '@/helpers/options'
import { CommentAPI, ReferralAPI, SourceAPI } from '@/api/backend-client'
import { Comment } from '@/api/model/comment'
import { v4 as uuid } from 'uuid'
import { Source } from '@/api/model/source'
import { CommentOrigin, CommentVisibility } from '@/api/types'

@Component
export default class UserInformation extends Vue {
    @Prop({ required: true }) readonly referral!: Referral
    value: Referral = null
    @Inject() readonly saveReferral: (referral: Referral) => Promise<void>
    @Inject() readonly reloadReferral: () => Promise<void>
    changeReferredByUserModal: boolean = false
    changeReferredByUserSubmitting: boolean = false
    changeAssignedUserModal: boolean = false
    changeAssignedUserSubmitting: boolean = false
    changeChannelModal: boolean = false
    changeChannelLoading: boolean = true
    changeChannelSubmitting: boolean = false
    availableSources: Source[] = []

    async mounted() {
        await this.reloadLocalValue()
    }

    @Watch('referral')
    async reloadLocalValue(): Promise<void> {
        this.value = this.referral.clone()
    }

    get availableAssignedUsers(): User[] {
        if (!this.referral || !this.referral.assignedToBranch) {
            return []
        }

        return this.$currentCompany.getAvailableUsers({
            branch: this.referral.assignedToBranch,
        })
    }

    get availableReferredUsers(): User[] {
        if (!this.referral || !this.referral.assignedToBranch) {
            return []
        }

        const availableUsers = this.$currentCompany.getAvailableUsers({
            branch: this.referral.referredByBranch,
        })

        if (availableUsers.findIndex((availableUser => availableUser.id === this.value.referredBy.id)) === -1) {
            availableUsers.push(this.value.referredBy)
        }

        return availableUsers.sort(((a, b) => a.name.localeCompare(b.name)))
    }

    get availableChannelOptions(): InputSelectOption[] {
        return generateChannelOptions()
    }

    @Watch('changeChannelModal')
    async reloadAvailableSources(): Promise<void> {
        if (!this.changeChannelModal) {
            return
        }

        this.changeChannelLoading = true

        try {
            if (this.value.assignedToBranch.company === null) {
                this.availableSources.splice(0)
                return
            }

            const sources = (await SourceAPI.getAll({ company: this.value.assignedToBranch.company.id, active: 1 }))

            this.availableSources.splice(0)
            this.availableSources.push(...sources)
        } finally {
            this.changeChannelLoading = false
        }
    }

    get availableSourceOptions(): InputSelectOption[] {
        return this.availableSources
            .filter(source => source.channel === this.value.channel)
            .map(source => ({
                label: source.name,
                value: source.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get newSource(): string|null {
        return this.value?.newSource?.id || null
    }

    set newSource(id: string|null) {
        this.value.newSource = this.availableSources.find(v => v.id === id) || null
    }

    get canEdit(): boolean {
        if (!this.$currentCompany || !this.referral || !this.referral.assignedToBranch) {
            return false
        }

        return this.$currentCompany.id === this.referral.assignedToBranch.company.id
    }

    get canEditReferredBy(): boolean {
        if (!this.canEdit || !this.$currentCompany.permissions) {
            return false
        }

        const permission = this.$currentCompany.permissions.find(permissionLookup => permissionLookup.name === 'referral.referredBy')

        return permission && permission.roles.includes(this.$currentUser.role)
    }

    get isEditable(): boolean {
        if (!this.referral || [
            'processed',
            'closed',
        ].includes(this.referral.status)) {
            return false
        }

        return true
    }

    get isAssigned(): boolean {
        return this.referral && this.referral.assignedTo !== null
    }

    async assignToMe(): Promise<void> {
        this.referral.assignedTo = this.$currentUser
        await this.saveReferral(this.referral)
        await this.reloadReferral()
    }

    async changeAssignedUser(): Promise<void> {
        this.changeAssignedUserSubmitting = true
        await this.saveReferral(Object.assign(this.referral, {
            assignedTo: this.value.assignedTo,
            assignedToBranch: this.value.assignedToBranch,
        }) as Referral)

        await this.reloadReferral()
        this.changeAssignedUserSubmitting = false
        this.changeAssignedUserModal = false
    }

    async changeReferredByUser(): Promise<void> {
        this.changeReferredByUserSubmitting = true
        await ReferralAPI.updateReferredBy(
            this.$currentCompany,
            this.value.id,
            this.value.referredBy,
            this.$currentUser,
        )

        await this.reloadReferral()
        this.changeReferredByUserSubmitting = false
        this.changeReferredByUserModal = false
    }

    async changeChannel(): Promise<void> {
        this.changeChannelSubmitting = true

        const oldChannel = this.referral.channel
        const newChannel = this.value.channel
        const channelChanged = oldChannel !== newChannel

        const oldSource = this.referral.newSource
        const newSource = this.value.newSource
        const sourceChanged = oldSource.id !== newSource.id

        if (channelChanged || sourceChanged) {
            await this.saveReferral(Object.assign({}, this.referral, {
                channel: newChannel,
                newSource,
            }) as Referral)

            let comment = ''
            if (channelChanged && sourceChanged) {
                comment = this.$t('forms.change-channel-form.comment.channelAndSource', {
                    user: this.$currentUser.name,
                    oldChannel: this.$t(`referral.channel.${oldChannel}`),
                    newChannel: this.$t(`referral.channel.${newChannel}`),
                    oldSource: oldSource.name,
                    newSource: newSource.name,
                }).toString()
            } else if (channelChanged) {
                comment = this.$t('forms.change-channel-form.comment.channel', {
                    user: this.$currentUser.name,
                    oldChannel: this.$t(`referral.channel.${oldChannel}`),
                    newChannel: this.$t(`referral.channel.${newChannel}`),
                }).toString()
            } else if (sourceChanged) {
                comment = this.$t('forms.change-channel-form.comment.source', {
                    user: this.$currentUser.name,
                    oldSource: oldSource.name,
                    newSource: newSource.name,
                }).toString()
            }

            await CommentAPI.create(Comment.fromData({
                referral: this.referral.resourceIdentifier,
                user: null,
                id: uuid(),
                comment,
                origin: CommentOrigin.SYSTEM,
                visibility: CommentVisibility.PUBLIC,
            }))

            await this.reloadReferral()
        }

        this.changeChannelSubmitting = false
        this.changeChannelModal = false
    }

    get nextAction(): Action {
        if (this.referral === null) {
            return null
        }

        return this.referral.actions
            .filter(action => action instanceof Action)
            .filter(action => !action.completed)
            .sort((a: Action, b: Action) => a.actionDate > b.actionDate ? 1 : -1)[0]
    }
}
